import React, { useCallback, useEffect, useRef, useState } from "react";
import { BellOff, RefreshCw, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { pushSupport, getDecision } from "@/lib/push";

/**
 * PushReconciliationBanner
 *
 * On every app open, detect the drift case:
 *   - Guest previously granted push (`em_push_decision === "granted"`)
 *   - But `Notification.permission` is now `denied` (they revoked in device settings)
 *   OR
 *   - Permission is `granted` but no active subscription (Chrome garbage-collected)
 *
 * Show a warm banner: "You turned off Andreas. Miss him already? Turn back on with one tap."
 *
 * Session-scoped dismiss so we don't nag on every navigation. Persists until
 * the browser tab is closed.
 */

const COPY = {
  en: {
    title: "Andreas is off-air",
    body: "You turned off notifications. Miss him already? Turn back on with one tap.",
    cta: "Turn back on",
    dismiss: "Not now",
  },
  fr: {
    title: "Andreas est hors-antenne",
    body: "Tu as désactivé les notifications. Il te manque déjà ? Réactive-le d'un tap.",
    cta: "Réactiver",
    dismiss: "Plus tard",
  },
  el: {
    title: "Ο Andreas είναι off-air",
    body: "Απενεργοποίησες τις ειδοποιήσεις. Σου λείπει ήδη; Ενεργοποίησέ τον με ένα tap.",
    cta: "Ενεργοποίηση",
    dismiss: "Όχι τώρα",
  },
  pl: {
    title: "Andreas offline",
    body: "Wyłączyłeś powiadomienia. Tęsknisz? Włącz go jednym tapnięciem.",
    cta: "Włącz ponownie",
    dismiss: "Nie teraz",
  },
};

const SESSION_DISMISS = "em_reconcile_dismiss";

const PushReconciliationBanner = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const { authReady, isAuthed } = useAuth();
  const [show, setShow] = useState(false);
  const checkedRef = useRef(false);

  const runCheck = useCallback(async () => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    if (!pushSupport()) return;

    // Was the guest previously subscribed? We only care about "drift" cases.
    const prevDecision = getDecision();
    if (prevDecision !== "granted") return;

    const perm = Notification.permission;
    let hasSub = false;
    try {
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        hasSub = !!sub;
      }
    } catch { /* ignore */ }

    // Drift detected: previously granted, but now blocked OR silently lost sub.
    const drift = perm === "denied" || (perm === "granted" && !hasSub);
    if (!drift) return;

    try {
      if (sessionStorage.getItem(SESSION_DISMISS) === "1") return;
    } catch { /* ignore */ }
    setShow(true);
  }, []);

  useEffect(() => {
    if (!authReady || !isAuthed) return;
    // Give the app a beat to render before dropping a banner on the guest
    const t = setTimeout(runCheck, 1600);
    return () => clearTimeout(t);
  }, [authReady, isAuthed, runCheck]);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS, "1"); } catch { /* ignore */ }
    setShow(false);
  };

  const turnBackOn = () => {
    // Delegate to the welcome modal — it handles both notifications and location.
    try {
      window.dispatchEvent(new Event("em-open-welcome"));
    } catch { /* ignore */ }
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[320]"
      role="alertdialog"
      aria-labelledby="reconcile-title"
      data-testid="push-reconcile-banner"
    >
      <div className="bg-background border border-amber-500/40 rounded-xl shadow-xl overflow-hidden animate-in fade-in-0 slide-in-from-bottom-6 duration-300">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-amber-500/15 text-amber-700 inline-flex items-center justify-center">
            <BellOff className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p id="reconcile-title" className="text-[13px] font-medium text-foreground">{c.title}</p>
            <p className="text-[12px] text-foreground/65 leading-snug mt-1">{c.body}</p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={turnBackOn}
                data-testid="push-reconcile-cta"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-[11px] tracking-[0.16em] uppercase"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {c.cta}
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="push-reconcile-dismiss"
                className="text-[11px] tracking-[0.16em] uppercase text-foreground/50 hover:text-foreground px-2 h-9"
              >
                {c.dismiss}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="w-7 h-7 rounded-md text-foreground/50 hover:text-foreground hover:bg-foreground/5 inline-flex items-center justify-center shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushReconciliationBanner;
